import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { FlamingoMessageService } from '@flamingo/service/flamingo-message.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CompanyType } from '../../../../../../../core/model/company.model';
import { PartnerPermission } from '../../../../../../../core/model/user.model';
import { TaxCodeDataService } from '../../../../../../../core/service/tax-code.data.service';
import { taxCodeValidator } from '../../../../../../../core/validator/tax-code.validator';
import { MainService } from '../../../../../main.service';
import { PartnerService } from '../../../partner.service';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';

@Component({
  selector: 'app-new-company-dialog',
  templateUrl: './new-company-dialog.component.html',
  styleUrls: ['./new-company-dialog.component.scss'],
  animations: [
    trigger('addressExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    fuseAnimations,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class NewCompanyDialogComponent implements OnInit {

  companyDefaultType: UntypedFormControl = new UntypedFormControl();
  CompanyType = CompanyType;

  companyFormGroup: UntypedFormGroup;
  sellerContactFormGroup: UntypedFormGroup;
  debtorContactFormGroup: UntypedFormGroup;
  addressFormGroup: UntypedFormGroup;

  showAddress = false;

  allowSellerCreate = false;
  allowDebtorCreate = false;

  isSaving = false;

  get sellerBusinessName(): AbstractControl {
    return this.companyFormGroup.get('businessName');
  }

  get sellerContactLastName(): AbstractControl {
    return this.sellerContactFormGroup.get('contactLastName');
  }

  get sellerContactEmail(): AbstractControl {
    return this.sellerContactFormGroup.get('contactEmail');
  }

  constructor(
    public taxCodeData: TaxCodeDataService,
    private _dialogRef: MatDialogRef<NewCompanyDialogComponent>,
    private _messageService: FlamingoMessageService,
    private _formBuilder: UntypedFormBuilder,
    private _partnerService: PartnerService,
    private _translationLoader: FuseTranslationLoaderService,
    private _translate: TranslateService,
    mainService: MainService,
  ) {
    this._translationLoader.loadTranslations(italian, english);

    this.allowSellerCreate = mainService.user.partnerPermissions.some(p => [PartnerPermission.All, PartnerPermission.SellerCreate].includes(p));
    this.allowDebtorCreate = mainService.user.partnerPermissions.some(p => [PartnerPermission.All, PartnerPermission.DebtorCreate].includes(p));

    if (this.allowSellerCreate) {
      this.companyDefaultType.setValue(CompanyType.seller);
    } else if (this.allowDebtorCreate) {
      this.companyDefaultType.setValue(CompanyType.debtor);
    }
  }

  ngOnInit(): void {
    this.companyFormGroup = this._formBuilder.group({
      businessName: new UntypedFormControl('', Validators.required),
      taxCodePrefix: new UntypedFormControl('IT', Validators.required),
      taxCode: new UntypedFormControl('', [Validators.required, taxCodeValidator]),
      legalForm: new UntypedFormControl(''),
    });

    this.sellerContactFormGroup = this._formBuilder.group({
      contactLastName: new UntypedFormControl('', Validators.required),
      contactFirstName: new UntypedFormControl(''),
      contactPhone: new UntypedFormControl(''),
      contactEmail: new UntypedFormControl('', [Validators.required, Validators.email]),
      notes: new UntypedFormControl(''),
    });

    this.debtorContactFormGroup = this._formBuilder.group({
      contactLastName: new UntypedFormControl('-', Validators.required),
      contactFirstName: new UntypedFormControl('-'),
      contactPhone: new UntypedFormControl('1234'),
      contactEmail: new UntypedFormControl('test@cashme.it', [Validators.required, Validators.email]),
      notes: new UntypedFormControl(''),
    });

    this.companyFormGroup.get('taxCodePrefix').valueChanges
      .pipe(
        tap(val => {
          this.companyFormGroup.get('taxCode').setValidators([Validators.required]);
          if (val === 'IT') {
            this.companyFormGroup.get('taxCode').setValidators([Validators.required, taxCodeValidator]);
          }
          this.companyFormGroup.get('taxCode').updateValueAndValidity();
        }),
      )
      .subscribe();

    this.addressFormGroup = this._formBuilder.group({
      address1: new UntypedFormControl(''),
      address2: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      state: new UntypedFormControl(''),
      country: new UntypedFormControl(''),
      zip: new UntypedFormControl(''),
      companyPhone: new UntypedFormControl(''),
      companyFax: new UntypedFormControl(''),
    });
  }

  checkError(value: AbstractControl): boolean {
    return value.invalid && (value.dirty || value.touched);
  }

  checkDisabled(): boolean {
    let disabled: boolean = this.isSaving || this.companyFormGroup.invalid || this.addressFormGroup.invalid;

    if (this.companyDefaultType.value === CompanyType.seller) {
      return disabled || this.sellerContactFormGroup.invalid;
    }

    return disabled;
  }

  onSubmit(): void {
    this.isSaving = true;

    this.companyDefaultType.disable();
    this.addressFormGroup.disable();
    this.sellerContactFormGroup.disable();
    this.companyFormGroup.disable();

    const contact = this.companyDefaultType.value === CompanyType.seller ? this.sellerContactFormGroup.getRawValue() : this.debtorContactFormGroup.getRawValue();

    this._partnerService.saveCompany(
      this.companyDefaultType.value,
      {
        ...this.companyFormGroup.getRawValue(),
        ...contact,
        ...this.addressFormGroup.getRawValue(),
      },
    )
      .pipe(
        tap(_ => this._dialogRef.close(true)),
        catchError(err => {
          this.companyDefaultType.enable();
          this.companyFormGroup.enable();
          this.sellerContactFormGroup.enable();
          this.addressFormGroup.enable();

          if (err.status === 409) {
            this.companyFormGroup.get('taxCode').setErrors({ forbidden: true });
          } else {
            const error = err.errorObj.error;

            let errorMsg = error.message;
            if (this._translate.currentLang === 'it' && !!error.messageIt) {
              errorMsg = error.messageIt;
            }

            this._messageService.showMessage(errorMsg);
          }
          this.isSaving = false;

          return EMPTY;
        }),
      )
      .subscribe();
  }
}
